import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { createSlideButton, makeId, MAX_SLIDE_BUTTONS } from "./homepageHero.constants";

function FieldError({ children }) {
  return children ? <div className="hero-field-error">{children}</div> : null;
}

function buttonPreviewStyle(button) {
  if (button.style === "OUTLINE") {
    return { color: button.textColor, backgroundColor: "transparent", borderColor: button.borderColor };
  }
  if (button.style === "TEXT") {
    return { color: button.textColor, backgroundColor: "transparent", borderColor: "transparent" };
  }
  return { color: button.textColor, backgroundColor: button.backgroundColor, borderColor: button.borderColor };
}

function ColorInput({ id, label, value, error, disabled, onChange }) {
  return (
    <div className="hero-button-field">
      <label htmlFor={id}>{label}</label>
      <div className="hero-button-color-input">
        <input id={`${id}-picker`} type="color" value={value} disabled={disabled} aria-label={`${label} picker`} onChange={(event) => onChange(event.target.value)} />
        <input id={id} value={value} disabled={disabled} className={error ? "is-invalid" : ""} onChange={(event) => onChange(event.target.value)} />
      </div>
      <FieldError>{error}</FieldError>
    </div>
  );
}

function SlideButtonCard({
  button,
  index,
  total,
  errors,
  language,
  expanded,
  disabled,
  onToggle,
  onChange,
  onDuplicate,
  onDelete,
  onMove,
}) {
  const prefix = `slide-button-${button.id}`;
  const label = language === "nl" ? button.label_nl : button.label;
  const labelField = language === "nl" ? "label_nl" : "label";
  const labelError = errors[labelField];
  const languageName = language === "nl" ? "Dutch" : "English";
  return (
    <article className={`hero-slide-button-card ${expanded ? "is-expanded" : ""}`}>
      <header className="hero-slide-button-card__header">
        <span className="hero-button-drag" aria-hidden="true">⋮⋮</span>
        <button type="button" className="hero-slide-button-card__identity" onClick={onToggle} aria-expanded={expanded}>
          <small>Button {index + 1}</small>
          <strong>{label || "Untitled button"}</strong>
          <span>{button.style === "TEXT" ? "Text / link" : button.style.toLowerCase()}</span>
        </button>
        <button type="button" className={`hero-inline-button-preview is-${button.style.toLowerCase()}`} style={buttonPreviewStyle(button)} onClick={(event) => { event.preventDefault(); event.stopPropagation(); }}>
          {label || "Button preview"}
        </button>
        <div className="hero-slide-button-card__actions">
          <button type="button" disabled={disabled || index === 0} onClick={() => onMove(-1)} aria-label={`Move button ${index + 1} up`}>↑</button>
          <button type="button" disabled={disabled || index === total - 1} onClick={() => onMove(1)} aria-label={`Move button ${index + 1} down`}>↓</button>
          <button type="button" disabled={disabled || total >= MAX_SLIDE_BUTTONS} onClick={onDuplicate}>Duplicate</button>
          <button type="button" disabled={disabled} className="is-danger" onClick={onDelete}>Delete</button>
          <button type="button" onClick={onToggle} aria-label={expanded ? "Collapse button editor" : "Expand button editor"}>{expanded ? "−" : "+"}</button>
        </div>
      </header>

      {expanded && (
        <div className="hero-slide-button-card__body">
          <div className="hero-button-fields-grid">
            <div className="hero-button-field">
              <label htmlFor={`${prefix}-${labelField}`}>Button label ({languageName}) *</label>
              <input id={`${prefix}-${labelField}`} maxLength="40" value={label || ""} disabled={disabled} className={labelError ? "is-invalid" : ""} onChange={(event) => onChange(labelField, event.target.value)} />
              <div className="hero-button-field__meta"><FieldError>{labelError}</FieldError><span>{(label || "").length} / 40</span></div>
            </div>
            <div className="hero-button-field hero-button-field--wide">
              <label htmlFor={`${prefix}-url`}>Destination URL *</label>
              <input id={`${prefix}-url`} maxLength="2048" placeholder="/route or https://example.com" value={button.url} disabled={disabled} className={errors.url ? "is-invalid" : ""} onChange={(event) => onChange("url", event.target.value)} />
              <FieldError>{errors.url}</FieldError>
            </div>
            <div className="hero-button-field">
              <label htmlFor={`${prefix}-style`}>Button style *</label>
              <select id={`${prefix}-style`} value={button.style} disabled={disabled} className={errors.style ? "is-invalid" : ""} onChange={(event) => onChange("style", event.target.value)}>
                <option value="FILLED">Filled</option>
                <option value="OUTLINE">Outline</option>
                <option value="TEXT">Text / link</option>
              </select>
              <FieldError>{errors.style}</FieldError>
            </div>
            <ColorInput id={`${prefix}-text-color`} label="Text color" value={button.textColor} error={errors.textColor} disabled={disabled} onChange={(value) => onChange("textColor", value)} />
            {button.style === "FILLED" && <ColorInput id={`${prefix}-background-color`} label="Background color" value={button.backgroundColor} error={errors.backgroundColor} disabled={disabled} onChange={(value) => onChange("backgroundColor", value)} />}
            {button.style !== "TEXT" && <ColorInput id={`${prefix}-border-color`} label="Border color" value={button.borderColor} error={errors.borderColor} disabled={disabled} onChange={(value) => onChange("borderColor", value)} />}
          </div>
          <label className="hero-button-new-tab">
            <input type="checkbox" checked={button.openInNewTab} disabled={disabled} onChange={(event) => onChange("openInNewTab", event.target.checked)} />
            <span><strong>Open in new tab</strong><small>Recommended for external destinations.</small></span>
          </label>
        </div>
      )}
    </article>
  );
}

export default function SlideButtonsEditor({ buttons = [], errors = [], disabled, onChange }) {
  const [expandedButtonId, setExpandedButtonId] = useState(buttons[0]?.id || null);
  const [buttonToDelete, setButtonToDelete] = useState(null);
  const [buttonLanguage, setButtonLanguage] = useState("en");
  const newButtonRef = useRef(null);

  useEffect(() => {
    const firstErrorIndex = errors.findIndex(Boolean);
    if (firstErrorIndex >= 0 && buttons[firstErrorIndex]) {
      setExpandedButtonId(buttons[firstErrorIndex].id);
      if (errors[firstErrorIndex]?.label_nl) setButtonLanguage("nl");
      else if (errors[firstErrorIndex]?.label) setButtonLanguage("en");
    }
  }, [errors, buttons]);

  const commit = (nextButtons) => onChange(nextButtons.map((button, index) => ({ ...button, order: index + 1 })));

  const addButton = () => {
    if (buttons.length >= MAX_SLIDE_BUTTONS) return;
    const button = createSlideButton(buttons.length + 1);
    commit([...buttons, button]);
    setExpandedButtonId(button.id);
    window.setTimeout(() => newButtonRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 0);
  };

  const updateButton = (index, field, value) => {
    commit(buttons.map((button, buttonIndex) => buttonIndex === index ? { ...button, [field]: value } : button));
  };

  const duplicateButton = (index) => {
    if (buttons.length >= MAX_SLIDE_BUTTONS) return;
    const copy = createSlideButton(index + 2, { ...buttons[index], id: makeId(), _isTemporary: true });
    const next = [...buttons];
    next.splice(index + 1, 0, copy);
    commit(next);
    setExpandedButtonId(copy.id);
  };

  const moveButton = (index, direction) => {
    const destination = index + direction;
    if (destination < 0 || destination >= buttons.length) return;
    const next = [...buttons];
    const [moved] = next.splice(index, 1);
    next.splice(destination, 0, moved);
    commit(next);
  };

  const deleteButton = () => {
    commit(buttons.filter((button) => button.id !== buttonToDelete.id));
    if (expandedButtonId === buttonToDelete.id) setExpandedButtonId(null);
    setButtonToDelete(null);
  };

  return (
    <section className="hero-form-section hero-slide-buttons-editor">
      <header className="hero-form-section__header hero-slide-buttons-editor__heading">
        <span className="hero-section-icon">↗</span>
        <div><h4>Slide buttons</h4><p>Add one or more actions to the hero slide.</p></div>
        <button type="button" className="hero-button hero-button--primary hero-add-slide-button" disabled={disabled || buttons.length >= MAX_SLIDE_BUTTONS} onClick={addButton}>＋ Add button</button>
      </header>

      <div className="hero-editor-language-switch" role="tablist" aria-label="Button label language">
        <button type="button" role="tab" aria-selected={buttonLanguage === "en"} className={buttonLanguage === "en" ? "is-active" : ""} onClick={() => setButtonLanguage("en")}>English</button>
        <button type="button" role="tab" aria-selected={buttonLanguage === "nl"} className={buttonLanguage === "nl" ? "is-active" : ""} onClick={() => setButtonLanguage("nl")}>Dutch</button>
      </div>

      {!buttons.length ? (
        <div className="hero-buttons-empty">
          <span aria-hidden="true">↗</span>
          <strong>No slide buttons added</strong>
          <p>Add a button when this slide needs a customer action.</p>
          <button type="button" className="hero-button hero-button--neutral" disabled={disabled} onClick={addButton}>＋ Add button</button>
        </div>
      ) : (
        <div className="hero-slide-button-list">
          {buttons.map((button, index) => (
            <div key={button.id} ref={index === buttons.length - 1 ? newButtonRef : null}>
              <SlideButtonCard
                button={button}
                index={index}
                total={buttons.length}
                errors={errors[index] || {}}
                language={buttonLanguage}
                expanded={expandedButtonId === button.id}
                disabled={disabled}
                onToggle={() => setExpandedButtonId(expandedButtonId === button.id ? null : button.id)}
                onChange={(field, value) => updateButton(index, field, value)}
                onDuplicate={() => duplicateButton(index)}
                onDelete={() => setButtonToDelete(button)}
                onMove={(direction) => moveButton(index, direction)}
              />
            </div>
          ))}
        </div>
      )}

      {buttons.length >= MAX_SLIDE_BUTTONS && <p className="hero-buttons-limit">Maximum {MAX_SLIDE_BUTTONS} buttons allowed.</p>}

      <Modal show={Boolean(buttonToDelete)} onHide={() => setButtonToDelete(null)} centered className="hero-confirm-modal">
        <Modal.Header closeButton><Modal.Title>Delete button?</Modal.Title></Modal.Header>
        <Modal.Body>Delete “{buttonToDelete?.label || "Untitled button"}” from this slide?</Modal.Body>
        <Modal.Footer>
          <button type="button" className="hero-modal-button hero-modal-button--neutral" onClick={() => setButtonToDelete(null)}>Cancel</button>
          <button type="button" className="hero-modal-button hero-modal-button--danger" onClick={deleteButton}>Delete button</button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}
